// Centralized status & signal color tokens. Keep in sync with badges.
//
// Two distinct semantic systems:
//
//   1. STATUS  -> "Undervalued" / "Fair" / "Overvalued"
//      Pure mathematical valuation classification. Used as text labels and
//      for the small Valuation pill, NOT for the headline color (a stock
//      can be "Undervalued" but still a value-trap).
//
//   2. SIGNAL  -> "Opportunity" / "Neutral" / "Risk"
//      Final user-facing recommendation that takes valuation, business
//      quality, growth and risk into account. THIS drives the overall
//      green/yellow/red color across the app so the visuals always agree
//      with the AI Opportunity Score.

const TOKENS = {
  green: {
    fg: "#3FAE89",
    bg: "rgba(63,174,137,0.14)",
    border: "rgba(63,174,137,0.35)",
    glow: "rgba(63,174,137,0.40)",
  },
  amber: {
    fg: "#E0B341",
    bg: "rgba(224,179,65,0.12)",
    border: "rgba(224,179,65,0.32)",
    glow: "rgba(224,179,65,0.35)",
  },
  red: {
    fg: "#E5484D",
    bg: "rgba(229,72,77,0.12)",
    border: "rgba(229,72,77,0.32)",
    glow: "rgba(229,72,77,0.35)",
  },
  // Phase 6.BI-17 — neutral / "unknown" token used ONLY for loading
  // states or unrecognised status values. Amber USED TO be the
  // fallback, which caused a transient orange dot to flash in the
  // analyze header badges before the backend response arrived (user
  // report: "small orange dot still appears in the header"). Amber
  // now means strictly "Fair" / "Neutral" — never "loading".
  neutral: {
    fg: "rgba(120, 120, 130, 0.85)",
    bg: "rgba(120, 120, 130, 0.10)",
    border: "rgba(120, 120, 130, 0.28)",
    glow: "rgba(120, 120, 130, 0.20)",
  },
};

export const STATUS = {
  Undervalued: {
    ...TOKENS.green,
    label: (upside) => `Undervalued by ${Math.abs(Number(upside) || 0).toFixed(1)}%`,
  },
  Fair: {
    ...TOKENS.amber,
    label: () => "Fairly valued",
  },
  Overvalued: {
    ...TOKENS.red,
    label: (upside) => `Overvalued by ${Math.abs(Number(upside) || 0).toFixed(1)}%`,
  },
};

export const SIGNAL = {
  Opportunity: {
    ...TOKENS.green,
    label: () => "Opportunity",
    subtitle: "Attractive entry · score & risk align",
  },
  Neutral: {
    ...TOKENS.amber,
    label: () => "Neutral",
    subtitle: "Watchlist · waiting for clearer setup",
  },
  Risk: {
    ...TOKENS.red,
    label: () => "Risk",
    subtitle: "Overvalued or weak fundamentals",
  },
};

// Phase 6.BI-17 — a synthetic "Loading" entry so consumers always
// get a properly-shaped token block when status/signal hasn't been
// resolved yet. Renders a small neutral grey dot instead of an amber
// one, eliminating the transient orange-dot flash in the analyze
// header during initial render.
export const LOADING_STATUS = {
  ...TOKENS.neutral,
  label: () => "—",
};

export function getStatusTokens(status) {
  return STATUS[status] || LOADING_STATUS;
}

export function getSignalTokens(signal) {
  // Same neutral-on-unknown rule for signal pills.
  return SIGNAL[signal] || { ...TOKENS.neutral, label: () => "—", subtitle: "" };
}

import { translate, getLocale } from "@/lib/i18n";

export function statusHeadline(status, upsidePct) {
  // Phase 7.UI.13b — locale-aware headline. We read from the i18n
  // dictionaries via `translate()` so the same Pick / Analyze hero text
  // renders in the user's selected language. Falls back to the legacy
  // English string when no translation key matches.
  const upAbs = Math.abs(Number(upsidePct) || 0).toFixed(1);
  if (status === "Undervalued") {
    return translate("analyze.headline.undervalued", { pct: upAbs }, getLocale());
  }
  if (status === "Overvalued") {
    return translate("analyze.headline.overvalued", { pct: upAbs }, getLocale());
  }
  if (status === "Fair") {
    return translate("analyze.headline.fairly_valued", undefined, getLocale());
  }
  // LOADING / unknown: keep the legacy "—" fallback.
  const tok = getStatusTokens(status);
  return tok.label(upsidePct);
}

/**
 * Resolve which tokens drive the overall color of a card / panel.
 * Always prefers `signal`. Falls back to legacy `status` for older payloads.
 * If neither is resolved yet (loading state) we return neutral grey, NOT
 * amber, so the analyze header doesn't flash an orange dot.
 */
export function getDisplayTokens(valuation) {
  if (!valuation) return LOADING_STATUS;
  if (valuation.signal) return getSignalTokens(valuation.signal);
  return getStatusTokens(valuation.status);
}
